import { useMemo } from 'react';
import { PatternChange } from '@/hooks/useDeviceMetrics';
import { Device } from '@/hooks/useTriggerStream';
import { colorForDevice } from './vizPalette';
import { makeDeviceLabel, windowTimeLabels } from './chartUtils';
import { useT } from '@/i18n/LanguageContext';
import { visualizeContent } from '@/i18n/content/visualize';

interface Props {
  changes: PatternChange[];
  devices: Device[];
}

const W = 1200;
const LANE = 34;
const LABEL_W = 130;
const MAX_LEGEND = 12;

/**
 * Pattern tapestry: one horizontal band per light sculpture. Every coloured
 * segment is a period in which a given light pattern was active.
 */
export function PatternTapestry({ changes, devices }: Props) {
  const t = useT(visualizeContent).charts;

  const { lanes, legend, timeLabels, tMin, tMax } = useMemo(() => {
    const rows = changes
      .map((c) => ({ id: c.device_id, pattern: c.pattern, ts: new Date(c.started_at).getTime() }))
      .filter((r) => Number.isFinite(r.ts) && r.pattern);
    if (!rows.length) {
      return {
        lanes: [] as Array<{ id: string; segments: Array<{ from: number; to: number; pattern: string }> }>,
        legend: [] as Array<{ pattern: string; total: number }>,
        timeLabels: [] as string[],
        tMin: 0,
        tMax: 1,
      };
    }

    const tMin = Math.min(...rows.map((r) => r.ts));
    const tMax = Date.now();

    const ids = Array.from(new Set(rows.map((r) => r.id))).sort();
    const lanes = ids.map((id) => {
      const own = rows.filter((r) => r.id === id).sort((a, b) => a.ts - b.ts);
      // Merge consecutive entries with the same pattern into one segment, so
      // hourly buckets read as calm bands instead of per-hour stripes.
      const segments: Array<{ from: number; to: number; pattern: string }> = [];
      own.forEach((r) => {
        const last = segments[segments.length - 1];
        if (last && last.pattern === r.pattern) {
          last.to = r.ts;
        } else {
          segments.push({ from: r.ts, to: r.ts, pattern: r.pattern });
        }
      });
      segments.forEach((s, i) => {
        s.to = segments[i + 1]?.from ?? tMax;
      });
      return { id, segments };
    });

    // Legend: patterns ranked by total active duration across all sculptures.
    const duration = new Map<string, number>();
    lanes.forEach((l) =>
      l.segments.forEach((s) => duration.set(s.pattern, (duration.get(s.pattern) ?? 0) + (s.to - s.from))),
    );
    const legend = Array.from(duration.entries())
      .map(([pattern, total]) => ({ pattern, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, MAX_LEGEND);

    return { lanes, legend, timeLabels: windowTimeLabels(tMin, tMax), tMin, tMax };
  }, [changes]);

  const sortedIds = useMemo(() => lanes.map((l) => l.id).sort(), [lanes]);
  const deviceLabel = makeDeviceLabel(devices, sortedIds, t.sculptureFallback);
  const patternColor = (pattern: string) => colorForDevice(`pattern:${pattern}`);

  const H = Math.max(LANE, lanes.length * LANE);
  const text = 'rgb(var(--site-fg) / 0.5)';
  const span = Math.max(1, tMax - tMin);
  const chartW = W - LABEL_W;
  const xFor = (ts: number) => LABEL_W + ((ts - tMin) / span) * chartW;

  return (
    <figure className="w-full" aria-label={t.tapestryAria}>
      <svg viewBox={`0 0 ${W} ${H + 30}`} className="w-full h-auto">
        {lanes.map((lane, li) => {
          const y = li * LANE + LANE * 0.18;
          const h = LANE * 0.64;
          return (
            <g key={lane.id}>
              <text x={0} y={y + h / 2 + 3} fontSize={10} fontFamily="ui-monospace, monospace" fill={text}>
                {deviceLabel(lane.id)}
              </text>
              <rect x={LABEL_W} y={y} width={chartW} height={h} fill="rgb(var(--site-fg) / 0.04)" />
              {lane.segments.map((s, si) => {
                const x1 = xFor(s.from);
                const x2 = Math.max(x1 + 0.75, xFor(s.to));
                return (
                  <rect key={si} x={x1} y={y} width={x2 - x1} height={h} fill={patternColor(s.pattern)}>
                    <title>{s.pattern}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}

        {timeLabels.map((lbl, i) => (
          <text
            key={i}
            x={LABEL_W + (chartW * i) / (timeLabels.length - 1)}
            y={H + 22}
            textAnchor={i === 0 ? 'start' : i === timeLabels.length - 1 ? 'end' : 'middle'}
            fontSize={10}
            fontFamily="ui-monospace, monospace"
            fill={text}
          >
            {lbl}
          </text>
        ))}

        {lanes.length === 0 && (
          <text x={W / 2} y={H / 2} textAnchor="middle" fontSize={14} fontFamily="ui-monospace, monospace" fill={text}>
            {t.noActivity}
          </text>
        )}
      </svg>

      {legend.length > 0 && (
        <figcaption className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 px-2 font-mono text-[10px] uppercase text-sitefg/55">
          {legend.map((l) => (
            <span key={l.pattern} className="inline-flex items-center gap-2 whitespace-nowrap">
              <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" aria-hidden="true">
                <rect x="0" y="0" width="10" height="10" fill={patternColor(l.pattern)} />
              </svg>
              {l.pattern}
            </span>
          ))}
        </figcaption>
      )}
    </figure>
  );
}
